/* ActivateTreatmentDialog — iter98.4
 *
 * The "Fit aligners & start treatment" confirmation dialog. Adapts its
 * tone based on whether the patient has ever logged in:
 *   • Online-booker (last_login_at set) → calm "You're unlocking their
 *     treatment dashboard" copy. WhatsApp invite is OFF by default
 *     (they already know the app — no re-onboarding spam).
 *   • Walk-in (last_login_at null) → "They don't have the portal yet"
 *     copy + WhatsApp invite ON by default + the doctor can preview /
 *     edit the message before sending. After confirmation we return a
 *     wa.me link the caller can open on the doctor's own phone in the
 *     clinic.
 *
 * The endpoint /api/cases/{id}/activation-context decides the
 * defaults (`default_send_whatsapp`, `default_message`) so the front
 * end stays "dumb" — we just render what the server tells us.
 */
import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import {
    CheckCircle, WhatsappLogo, Phone, User as UserIcon, Sparkle, Lock,
} from "@phosphor-icons/react";
import { toast } from "sonner";

export default function ActivateTreatmentDialog({ open, onOpenChange, caseId, onActivated }) {
    const [ctx, setCtx] = useState(null);
    const [sendWa, setSendWa] = useState(false);
    const [message, setMessage] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!open || !caseId) return;
        setCtx(null);
        setError(null);
        api.get(`/cases/${caseId}/activation-context`)
            .then((r) => {
                setCtx(r.data);
                setSendWa(r.data.default_send_whatsapp);
                setMessage(r.data.default_message || "");
            })
            .catch((e) => setError(e?.response?.data?.detail || "Couldn't load activation details."));
    }, [open, caseId]);

    const onConfirm = async () => {
        setSubmitting(true);
        try {
            const payload = { send_whatsapp_invite: sendWa, message: sendWa ? message : null };
            const { data } = await api.post(`/cases/${caseId}/activate-treatment`, payload);
            toast.success("Treatment activated · patient dashboard is now live");
            if (data.wa_link) {
                // Open the WhatsApp deep link so the doctor can send it
                // from their own phone right there in the clinic.
                window.open(data.wa_link, "_blank", "noopener,noreferrer");
            }
            onActivated?.(data);
            onOpenChange(false);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Activation failed. Please try again.");
        } finally {
            setSubmitting(false);
        }
    };

    const isWalkIn = ctx && !ctx.patient?.has_logged_in;
    const firstName = ((ctx?.patient?.name || "").split(" ") || ["the patient"])[0] || "the patient";

    return (
        <Dialog open={open} onOpenChange={(o) => !submitting && onOpenChange(o)}>
            <DialogContent className="max-w-lg p-0 overflow-hidden gap-0" data-testid="activate-treatment-dialog">
                {/* HEADER */}
                <div className="px-6 pt-5 pb-4 border-b border-border bg-gradient-to-r from-emerald-50 via-emerald-50/50 to-transparent">
                    <div className="flex items-center gap-2.5 pr-7">
                        <span className="w-10 h-10 rounded-xl bg-emerald-600 text-white grid place-items-center shrink-0">
                            <Sparkle size={18} weight="bold" />
                        </span>
                        <div>
                            <div className="font-display text-base font-bold leading-tight">Fit aligners & start treatment</div>
                            <div className="text-[11.5px] text-muted-foreground mt-0.5">
                                Unlock {firstName}&apos;s treatment dashboard.
                            </div>
                        </div>
                    </div>
                </div>

                {/* BODY */}
                <div className="px-6 py-5 space-y-4">
                    {error && (
                        <div className="text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded-lg p-3">{error}</div>
                    )}

                    {!ctx && !error && (
                        <div className="text-xs text-muted-foreground italic py-4">Loading…</div>
                    )}

                    {ctx && (
                        <>
                            {/* Patient summary chip */}
                            <div className="rounded-xl border border-border bg-secondary/30 p-3">
                                <div className="flex items-center gap-2.5">
                                    <span className="w-8 h-8 rounded-full bg-primary/10 text-primary grid place-items-center shrink-0">
                                        <UserIcon size={14} weight="duotone" />
                                    </span>
                                    <div className="flex-1 min-w-0">
                                        <div className="text-sm font-semibold truncate">{ctx.patient.name}</div>
                                        <div className="text-[11px] text-muted-foreground flex items-center gap-1 mt-0.5">
                                            <Phone size={10} weight="bold" /> {ctx.patient.phone || "—"}
                                            <span className="opacity-50">·</span>
                                            <span
                                                className={`px-1.5 py-0.5 rounded text-[9.5px] font-bold uppercase tracking-wider border ${
                                                    isWalkIn
                                                        ? "bg-amber-50 text-amber-800 border-amber-200"
                                                        : "bg-emerald-50 text-emerald-800 border-emerald-200"
                                                }`}
                                            >
                                                {isWalkIn ? "Walk-in · never logged in" : "Online · uses portal"}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* What this unlocks */}
                            <div className="rounded-xl border border-emerald-200 bg-emerald-50/40 p-3.5">
                                <div className="text-[10px] font-bold uppercase tracking-[0.14em] text-emerald-800 mb-1.5">
                                    What unlocks
                                </div>
                                <ul className="text-[12px] text-emerald-900 space-y-1 leading-snug">
                                    <UnlockItem text="Full wear schedule (Aligner 1 → final)" />
                                    <UnlockItem text="Switch-to-next-aligner button + compliance tracking" />
                                    <UnlockItem text="Daily wear-time logging + photo check-ins" />
                                    <UnlockItem text="Stage 1 starts now — switch dates anchor on today's fitting" />
                                </ul>
                            </div>

                            {/* WhatsApp invite toggle */}
                            <div className="rounded-xl border border-border bg-card p-3.5">
                                <div className="flex items-start justify-between gap-3">
                                    <div className="flex items-start gap-2">
                                        <span className="w-8 h-8 rounded-lg bg-emerald-50 text-emerald-700 grid place-items-center shrink-0">
                                            <WhatsappLogo size={14} weight="duotone" />
                                        </span>
                                        <div>
                                            <Label className="text-sm font-semibold cursor-pointer" htmlFor="send-wa-toggle">
                                                Send WhatsApp invite
                                            </Label>
                                            <div className="text-[11px] text-muted-foreground mt-0.5 leading-snug">
                                                {isWalkIn
                                                    ? `${firstName} hasn't logged into the portal yet — send them the link with setup instructions.`
                                                    : `${firstName} already uses the portal. A quick nudge is optional.`}
                                            </div>
                                        </div>
                                    </div>
                                    <Switch
                                        id="send-wa-toggle"
                                        checked={sendWa}
                                        onCheckedChange={setSendWa}
                                        data-testid="activate-wa-toggle"
                                    />
                                </div>

                                {sendWa && (
                                    <div className="mt-3 space-y-1.5">
                                        <Label className="text-[10px] uppercase tracking-[0.14em] font-bold text-muted-foreground">
                                            Message preview
                                        </Label>
                                        <Textarea
                                            value={message}
                                            onChange={(e) => setMessage(e.target.value)}
                                            rows={3}
                                            className="text-xs resize-none bg-secondary/30"
                                            data-testid="activate-wa-message"
                                        />
                                        <p className="text-[10.5px] text-muted-foreground italic">
                                            Opens on your phone via WhatsApp Web/App in a new tab so you can send it from there.
                                        </p>
                                    </div>
                                )}
                            </div>
                        </>
                    )}
                </div>

                {/* FOOTER */}
                <div className="px-6 py-4 bg-secondary/30 border-t border-border flex items-center justify-between gap-2">
                    <span className="text-[10.5px] text-muted-foreground flex items-center gap-1.5">
                        <Lock size={11} weight="bold" /> Once activated, this cannot be undone.
                    </span>
                    <div className="flex items-center gap-2">
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => onOpenChange(false)}
                            disabled={submitting}
                            className="rounded-full px-4"
                            data-testid="activate-cancel"
                        >
                            Cancel
                        </Button>
                        <Button
                            size="sm"
                            onClick={onConfirm}
                            disabled={submitting || !ctx || !ctx.can_activate}
                            className="rounded-full px-4 gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white"
                            data-testid="activate-confirm"
                        >
                            <CheckCircle size={13} weight="bold" />
                            {submitting ? "Activating…" : "Activate & fit"}
                        </Button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}

function UnlockItem({ text }) {
    return (
        <li className="flex items-start gap-1.5">
            <CheckCircle size={11} weight="fill" className="text-emerald-600 shrink-0 mt-0.5" />
            <span>{text}</span>
        </li>
    );
}
